import React, { useEffect, useRef } from 'react';
import { getSocialLinks } from '../services/api';
import { Instagram, ExternalLink, Circle } from 'lucide-react';

// Instagram reel URLs to embed
const INSTAGRAM_REELS = [
  'https://www.instagram.com/reel/DEjT3yPueJI/',
  'https://www.instagram.com/reel/DD-nAgFvhWW/',
  'https://www.instagram.com/reel/DDwlk-4O1Py/'
];

const Pickleball = () => {
  const [socialLinks, setSocialLinks] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const embedsLoaded = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const linksData = await getSocialLinks();
        setSocialLinks(linksData);
      } catch (error) {
        console.error('Error fetching social links:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Load Instagram embed script
  useEffect(() => {
    if (!embedsLoaded.current && !loading) {
      embedsLoaded.current = true;
      
      // Check if script already exists
      if (!document.getElementById('instagram-embed-script')) {
        const script = document.createElement('script');
        script.id = 'instagram-embed-script';
        script.src = '//www.instagram.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
        
        script.onload = () => {
          if (window.instgrm) {
            window.instgrm.Embeds.process();
          }
        };
      } else if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="py-16 md:py-24 bg-gradient-to-b from-white via-purple-50 to-white relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-12 relative z-10 text-center">
          <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600 text-sm md:text-base">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full blur-3xl opacity-20" />
      <div className="absolute bottom-10 left-10 w-24 md:w-48 h-24 md:h-48 bg-gradient-to-br from-pink-200 to-orange-200 rounded-full blur-3xl opacity-20" />
      
      <div className="container mx-auto px-4 md:px-12 relative z-10">
        {/* Section Header */}
        <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-3 mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center rounded-xl md:rounded-2xl shadow-lg shadow-purple-500/30 relative">
              <Circle size={20} className="md:hidden text-white" strokeWidth={2.5} />
              <Circle size={28} className="hidden md:block text-white" strokeWidth={2.5} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="grid grid-cols-2 gap-0.5">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-purple-300 rounded-full opacity-60"></div>
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-purple-300 rounded-full opacity-60"></div>
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-purple-300 rounded-full opacity-60"></div>
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-purple-300 rounded-full opacity-60"></div>
                </div>
              </div>
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-3 md:mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Pickleball Passion
          </h2>
          <p className="text-sm md:text-lg text-gray-600 font-light leading-relaxed mb-4 md:mb-6 px-2">
            When I&apos;m not building tech or investing in startups, you&apos;ll find me on the court.
          </p>
          {socialLinks && (
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-lg md:rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 group shadow-lg hover:shadow-xl hover:scale-105 text-sm md:text-base"
            >
              <Instagram size={18} className="md:hidden" />
              <Instagram size={20} className="hidden md:block" />
              <span>Follow @sadhwani.pickleball</span>
              <ExternalLink size={14} className="md:hidden group-hover:translate-x-1 transition-transform duration-300" />
              <ExternalLink size={16} className="hidden md:block group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          )}
        </div>

        {/* Embedded Instagram Reels - Responsive Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {INSTAGRAM_REELS.map((reelUrl, index) => (
              <div 
                key={index} 
                className="flex justify-center w-full"
              >
                <div className="w-full max-w-[328px] sm:max-w-none">
                  <blockquote 
                    className="instagram-media" 
                    data-instgrm-captioned 
                    data-instgrm-permalink={`${reelUrl}?utm_source=ig_embed&utm_campaign=loading`}
                    data-instgrm-version="14"
                    style={{ 
                      background: '#FFF',
                      border: '0',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      margin: '0 auto',
                      maxWidth: '100%',
                      minWidth: '280px',
                      padding: '0',
                      width: '100%'
                    }}
                  >
                    <div style={{ padding: '16px' }}>
                      <a 
                        href={`${reelUrl}?utm_source=ig_embed&utm_campaign=loading`}
                        style={{ 
                          background: '#FFFFFF',
                          lineHeight: '0',
                          padding: '0 0',
                          textAlign: 'center',
                          textDecoration: 'none',
                          width: '100%'
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 animate-pulse"></div>
                          <div>
                            <div className="h-3 bg-gray-200 rounded w-24 mb-1 animate-pulse"></div>
                            <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                          </div>
                        </div>
                        <div className="aspect-[9/16] bg-gray-100 rounded-lg flex items-center justify-center">
                          <div className="text-center">
                            <Instagram size={36} className="mx-auto text-gray-400 mb-2" />
                            <p className="text-xs text-gray-500">Loading Reel...</p>
                          </div>
                        </div>
                      </a>
                    </div>
                  </blockquote>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* View Full Profile CTA */}
        <div className="text-center mt-8 md:mt-12">
          <p className="text-gray-500 text-xs md:text-sm mb-3 md:mb-4">Want to see more? Check out my full profile</p>
          {socialLinks && (
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 border-2 border-purple-500 text-purple-600 font-semibold rounded-lg md:rounded-xl hover:bg-purple-500 hover:text-white transition-all duration-300 group text-sm md:text-base"
            >
              <span>View Full Profile</span>
              <ExternalLink size={14} className="md:hidden group-hover:translate-x-1 transition-transform duration-300" />
              <ExternalLink size={16} className="hidden md:block group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pickleball;
